import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';
import type {
  Connection,
  ConnectionListResponse
} from '@utils/types/connections/connection.ts';

async function fetchConnections(): Promise<Connection[]> {
  const { data } = await Axios.get<ConnectionListResponse>(
    GET_URL().connections
  );
  return data.result;
}

export function useGetConnections(enabled: boolean = true) {
  return useQuery<Connection[], AxiosError<{ detail?: string }>>(
    QueryKeys.connections(),
    fetchConnections,
    {
      enabled: enabled && !!TokenExtension.get()?.string && !IS_ON_PREM,
      useErrorBoundary: false,
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
      // ``publishing-not-enabled`` from #4099's ``require_publishing_enabled``
      // dependency means the whole feature is off for this org — not an
      // error worth retrying. Every other failure uses the default retry.
      retry: (failureCount, error) => {
        if (isPublishingDisabledError(error)) return false;
        return failureCount < 3;
      }
    }
  );
}

/**
 * Backend returns 404 + ``detail: "publishing-not-enabled"`` when the
 * organization setting is off. #2302 asks us to hide the whole
 * Connections section in that case; this helper is what the UI
 * branches on.
 */
export function isPublishingDisabledError(
  error: unknown
): error is AxiosError<{ detail?: string }> {
  if (!(error instanceof AxiosError)) return false;
  if (error.response?.status !== 404) return false;
  const detail = (error.response?.data as { detail?: string } | undefined)
    ?.detail;
  return detail === 'publishing-not-enabled';
}
